import React from "react";
import {
  XMarkIcon,
  PencilSquareIcon,
  ClockIcon,
  ArrowPathIcon,
  ShieldExclamationIcon,
  UserIcon,
  TagIcon,
  ServerStackIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { INCIDENT_STATUS_OPTIONS } from "../../constants/AppConstants";
import ApiLoader from "../ui/ApiLoader";

const EditIncidentModal = ({
  show,
  selectedIncident,
  onClose,
  editStatus,
  setEditStatus,
  editNotes,
  setEditNotes,
  onSaveStatus,
  savingStatus,
  incidentLogs,
  loadingLogs,
  getSeverityColor,
  getStatusColor,
  formatDate,
}) => {
  if (!show || !selectedIncident) return null;

  const handleClose = () => {
    onClose();
    setEditStatus("");
    setEditNotes("");
  };

  const isSavingDisabled =
    !editStatus ||
    !editNotes.trim() ||
    editStatus === selectedIncident.status ||
    savingStatus;

  const getEmployeeDisplay = () => {
    const info = selectedIncident.employee_info;
    if (!info?.employee_name) return "Unknown";
    let display = info.employee_name;
    if (info.designation && info.designation !== "N/A")
      display += `, ${info.designation}`;
    if (info.department && info.department !== "N/A")
      display += ` · ${info.department}`;
    return display;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal Container */}
      <div className="relative w-full max-w-5xl max-h-[80vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 dark:border-gray-700">

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-700 to-primary-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
              <ShieldExclamationIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white leading-tight line-clamp-1">
                {selectedIncident.title}
              </h2>
              <p className="text-xs text-white capitalize mt-0.5">
                {selectedIncident.incident_type?.replace(/_/g, " ")} &nbsp;·&nbsp; #{selectedIncident.incident_no}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/25 flex items-center justify-center text-white transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* ── Body (two-column) ── */}
        <div className="flex flex-1 overflow-hidden">

          {/* Left column – incident info */}
          <div className="flex-1 overflow-y-auto p-5 space-y-4 border-r border-gray-200 dark:border-gray-700">

            {/* Quick-stat chips */}
            <div className="flex flex-wrap gap-2">
              <Chip
                icon={<TagIcon className="w-3.5 h-3.5" />}
                label="Status"
                value={(selectedIncident.status || "").replace(/_/g, " ").toUpperCase()}
                colorClass={getStatusColor(selectedIncident.status)}
              />
              <Chip
                icon={<ShieldExclamationIcon className="w-3.5 h-3.5" />}
                label="Severity"
                value={(selectedIncident.severity || "").toUpperCase()}
                colorClass={getSeverityColor(selectedIncident.severity)}
              />
            </div>

            {/* Details card */}
            <Card title="Incident Details">
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <Field label="Incident No" value={selectedIncident.incident_no} />
                <Field
                  label="Employee"
                  value={getEmployeeDisplay()}
                  icon={<UserIcon className="w-3 h-3" />}
                />
                <Field label="Category" value={selectedIncident.incident_type?.replace(/_/g, " ")} />
                <Field label="Title" value={selectedIncident.title} />
              </div>
            </Card>

            {/* Description */}
            <Card title="Description">
              <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {selectedIncident.description || <span className="italic text-gray-400">No description provided.</span>}
              </p>
            </Card>

            {/* Affected Systems */}
            {selectedIncident.affected_systems &&
              String(selectedIncident.affected_systems).trim().length > 0 && (
                <Card title="Affected Systems" icon={<ServerStackIcon className="w-4 h-4" />}>
                  <div className="flex flex-wrap gap-2">
                    {String(selectedIncident.affected_systems)
                      .split(",")
                      .map((s) => s.trim())
                      .filter((s) => s.length > 0)
                      .map((system, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-300 dark:border-gray-500 shadow-sm"
                        >
                          <ServerStackIcon className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          {system}
                        </span>
                      ))}
                  </div>
                </Card>
              )}
          </div>

          {/* Right column – actions + history */}
          <div className="w-80 shrink-0 flex flex-col overflow-hidden">

            {/* Update Status form */}
            <div className="p-5 border-b border-gray-200 dark:border-gray-700 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <PencilSquareIcon className="w-4 h-4 text-primary-600" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Update Status</h3>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  New Status <span className="text-red-500">*</span>
                </label>
                <select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
                >
                  <option value="">Select new status…</option>
                  {INCIDENT_STATUS_OPTIONS.filter(
                    (opt) => opt.value !== selectedIncident.status
                  ).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Action Notes <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none transition"
                  placeholder="Describe the action taken…"
                />
              </div>

              <button
                onClick={onSaveStatus}
                disabled={isSavingDisabled}
                className="w-full py-2.5 rounded-lg bg-primary-700 hover:bg-primary-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold flex items-center justify-center gap-2 transition-colors shadow-sm"
              >
                {savingStatus ? (
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                )}
                {savingStatus ? "Saving…" : "Save Status"}
              </button>
            </div>

            {/* Action History */}
            <div className="flex-1 overflow-y-auto p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center">
                    <ClockIcon className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Action History</h3>
                </div>
                {incidentLogs && incidentLogs.length > 0 && (
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-900 dark:text-primary-100">
                    {incidentLogs.length} {incidentLogs.length === 1 ? "entry" : "entries"}
                  </span>
                )}
              </div>

              {loadingLogs ? (
                <ApiLoader label="Loading history…" />
              ) : incidentLogs && incidentLogs.length > 0 ? (
                <div className="relative">
                  {/* vertical line */}
                  <span className="absolute left-3 top-3 bottom-3 w-0.5 bg-gradient-to-b from-primary-300 via-primary-200 to-transparent dark:from-primary-700 dark:via-primary-800" />
                  <div className="space-y-3">
                    {incidentLogs.map((log, idx) => (
                      <HistoryItem
                        key={log.id || idx}
                        log={log}
                        idx={idx}
                        total={incidentLogs.length}
                        getStatusColor={getStatusColor}
                        formatDate={formatDate}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
                    <ClockIcon className="w-6 h-6 text-gray-300 dark:text-gray-600" />
                  </div>
                  <p className="text-xs font-medium text-gray-400 dark:text-gray-500">No actions recorded yet</p>
                  <p className="text-[10px] text-gray-300 dark:text-gray-600 mt-0.5">Status changes will appear here</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="flex items-center justify-end gap-3 px-6 py-3 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Helper Components ====================

const Card = ({ title, icon, children }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 overflow-hidden">
    <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      {icon && <span className="text-gray-500 dark:text-gray-400">{icon}</span>}
      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {title}
      </h4>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Field = ({ label, value, icon }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5">
      {label}
    </p>
    <p className="text-xs font-medium text-gray-900 dark:text-gray-100 capitalize flex items-center gap-1">
      {icon && <span className="text-gray-400">{icon}</span>}
      {value || <span className="text-gray-400 italic">—</span>}
    </p>
  </div>
);

const Chip = ({ icon, label, value, colorClass }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${colorClass}`}>
    {icon}
    <span className="text-gray-500 dark:text-gray-400 font-normal">{label}:</span>
    {value}
  </span>
);

const HistoryItem = ({ log, idx, total, getStatusColor, formatDate }) => (
  <div className="relative flex gap-3 pl-7">
    {/* dot */}
    <span className={`absolute left-2 top-3 w-2.5 h-2.5 rounded-full border-2 z-10 ${
      idx === 0
        ? "border-primary-500 bg-primary-500"
        : "border-gray-400 bg-white dark:bg-gray-900"
    }`} />

    {/* card */}
    <div className={`flex-1 rounded-xl border p-3 mb-1 shadow-sm ${
      idx === 0
        ? "bg-white dark:bg-gray-800 border-primary-300 dark:border-primary-600 ring-1 ring-primary-200 dark:ring-primary-700"
        : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
    }`}>
      {/* status transition row */}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border ${getStatusColor(log.old_status)}`}>
          {(log.old_status || "—").replace(/_/g, " ")}
        </span>
        <span className="flex items-center">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </span>
        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border ${getStatusColor(log.new_status)}`}>
          {(log.new_status || "—").replace(/_/g, " ")}
        </span>
        {idx === 0 && (
          <span className="ml-auto text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-primary-600 text-white">LATEST</span>
        )}
      </div>

      {/* notes */}
      {log.notes && (
        <p className="text-[11px] text-gray-700 dark:text-gray-200 leading-relaxed mb-2 pl-2">
          {log.notes}
        </p>
      )}

      {/* footer */}
      <div className="flex flex-col gap-0.5 pt-1.5 border-t border-gray-100 dark:border-gray-700">
        <span className="flex items-center gap-1 text-[10px] font-medium text-gray-600 dark:text-gray-300 truncate">
          <UserIcon className="w-3 h-3 shrink-0" />
          <span className="truncate">{log.performed_by || "Unknown"}</span>
        </span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums pl-4">
          {formatDate(log.created_at)}
        </span>
      </div>
    </div>
  </div>
);

export default EditIncidentModal;
